import { useMemo } from "react";
import * as THREE from "three";
import { tokens } from "../../styles/tokens";

/**
 * ADR 312 — the imported structure drawn inside the model-calibration workbench,
 * as the FIXED reference the operator drags the GLB onto.
 *
 * The polarity is deliberate and one-way: the structure lives in the map frame —
 * the frame live poses and every future import arrive in, kept equal to the
 * warehouse frame by construction (`map_to_warehouse` identity, ADR 98) — so it
 * never moves. The model is decoration that gets placed; `warehouse.transform`
 * exists for exactly that.
 *
 * One merged BufferGeometry, one draw call: a real site is tens of thousands of
 * bins, and one object per footprint is the instancing mistake ADR 199 already
 * paid for. Footprints are convex quads from the import, so a triangle fan per
 * ring is exact.
 */

/** Triangulate `[[x,y],…]` rings into one flat position array, fan per ring,
 *  at the given scene height. Pure — unit-testable without a canvas. */
export function footprintPositions(
  footprints: ReadonlyArray<ReadonlyArray<ReadonlyArray<number>>>, y = 0.05,
): Float32Array {
  let tris = 0;
  for (const f of footprints) if (f.length >= 3) tris += f.length - 2;
  const out = new Float32Array(tris * 9);
  let o = 0;
  for (const f of footprints) {
    if (f.length < 3) continue;
    const [x0, z0] = f[0];
    for (let i = 1; i + 1 < f.length; i++) {
      out[o++] = x0;      out[o++] = y; out[o++] = z0;
      out[o++] = f[i][0]; out[o++] = y; out[o++] = f[i][1];
      out[o++] = f[i + 1][0]; out[o++] = y; out[o++] = f[i + 1][1];
    }
  }
  return out;
}

export function StructureFootprints({ footprints }: {
  footprints: ReadonlyArray<ReadonlyArray<ReadonlyArray<number>>>;
}) {
  const geometry = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(footprintPositions(footprints), 3));
    return g;
  }, [footprints]);

  if (footprints.length === 0) return null;
  return (
    <mesh geometry={geometry}>
      <meshBasicMaterial color={tokens.blue[300]} transparent opacity={0.4}
                         depthWrite={false} side={THREE.DoubleSide} />
    </mesh>
  );
}
